
import { Component, Ref, Vue } from 'vue-property-decorator'
import StudentPersonalStudyCategoryProgress from '@/components/templates/manage/StudentPersonalStudyCategoryProgress.vue'
import ModalSelectGrade from '@/views/modal/ModalSelectGrade.vue'
import ModalSelectSubject from '@/views/modal/ModalSelectSubject.vue'
import { LearningProgressList } from '@/models/manage/LearningProgressList'
import { SelectItem } from '@/models/SelectItem'
import { GradeCode } from '@/const/GradeCode'
import { SubjectCode } from '@/const/SubjectCode'
import { LearningProgress } from '@/const/LearningProgress'
import { UsersApi } from '@/api/methods/UsersApi'
import { StudyCategoriesApi } from '@/api/methods/StudyCategoriesApi'
import { CurriculumUnitsApi } from '@/api/methods/manage/CurriculumUnitsApi'

/**
 * 生徒別学習進捗
 */
@Component({
  components: {
    StudentPersonalStudyCategoryProgress,
    ModalSelectGrade,
    ModalSelectSubject,
  },
})
export default class StudentStudyCategoryProgress extends Vue {
  /** 生徒別学習進捗レイアウト */
  @Ref()
  studentPersonalStudyCategoryProgress!: StudentPersonalStudyCategoryProgress

  /** フィルターDisabled時の固定値 */
  private readonly OPTION_DISABLED = { value: '', label: '教材がありません' }

  /** 生徒ニックネーム */
  private studentName = ''

  /** 学年フィルターの選択肢 */
  private filterGradeList: SelectItem[] = []

  /** 教科フィルターの選択肢 */
  private filterSubjectList: SelectItem[] = []

  /** 教材フィルターの選択肢 */
  private filterStudyCategoryList: SelectItem[] = []

  /** 教科フィルターの初期値 */
  private defaultSubjectCode = SubjectCode.LIST[0].code

  /** 単元別進捗一覧 */
  private progressCurriculumUnits: LearningProgressList.CurriculumUnit[] = []

  /** 教材選択ボタンのDisabled制御 */
  private isDisabledSelectStudyCategory = false

  /** 遷移元の情報を復元 */
  private prevPage = this.$prevPage.restorePage()

  /**
   * パラメータから取得した生徒ID
   *
   * @returns 生徒ID
   */
  private get studentId(): number {
    return Number(this.$route.params.userId)
  }

  /**
   * クエリ情報から取得した学年コード
   *
   * @returns 学年コード
   */
  private get queryGradeCode(): string | undefined {
    const gradeCode = this.prevPage?.query.gradeCode

    // クエリに学年コードが存在しない場合、undefined
    if (!gradeCode || typeof gradeCode !== 'string') {
      return undefined
    }

    return gradeCode
  }

  /**
   * クエリ情報から取得した教科コード
   *
   * @returns 教科コード
   */
  private get querySubjectCode(): string | undefined {
    const subjectCode = this.prevPage?.query.subjectCode

    // クエリに学年コードが存在しない場合、undefined
    if (!subjectCode || typeof subjectCode !== 'string') {
      return undefined
    }

    return subjectCode
  }

  /**
   * クエリ情報から取得した教材ID
   *
   * @returns 教材ID
   */
  private get queryStudyCategoryId(): string | undefined {
    const studyCategoryId = this.prevPage?.query.studyCategoryId

    // クエリに教材IDが存在しない場合、undefinedを返す
    if (!studyCategoryId || typeof studyCategoryId !== 'string') {
      return undefined
    }

    return studyCategoryId
  }

  /**
   * 初期処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()

    // 生徒情報取得
    const student = await UsersApi.getStudentInformation(this.studentId)

    // ニックネーム設定
    this.studentName = student.nickname

    // 学年一覧取得(フィルター設定)
    this.getGradeList()

    // 教科一覧取得(フィルター設定)
    this.getSubjectList()

    // 教材一覧取得(フィルター設定)
    await this.getStudyCategories(this.selectedGradeCode(), this.selectedSubjectCode())

    // 教科が選択されていない場合(教材が存在しない)、処理を終了
    if (!this.selectedStudyCategoryCode()) {
      this.$loading.complete()
      return
    }

    // 単元別進捗一覧設定
    await this.getCurriculumUnits()

    this.$loading.complete()
  }

  /**
   * 学年一覧取得(フィルター設定)
   */
  private getGradeList(): void {
    // 学年一覧を成形
    const gradeList = GradeCode.LIST.map((grade) => {
      return {
        value: grade.code,
        label: grade.label,
      }
    })

    // 学年フィルター設定
    this.filterGradeList = gradeList

    // クエリ情報から学年を取得
    const grade = GradeCode.findByCode(this.queryGradeCode || '')

    // 学年フィルターの初期選択値を設定
    this.studentPersonalStudyCategoryProgress.setCurrentGradeCode(grade?.code || GradeCode.LIST[0].code)
  }

  /**
   * 教科一覧取得(フィルター設定)
   */
  private getSubjectList(): void {
    // 教科一覧を成形
    const subjectList = SubjectCode.LIST.map((subject) => {
      return {
        value: subject.code,
        label: subject.label,
      }
    })

    // フィルターに設定
    this.filterSubjectList = subjectList

    // クエリ情報から教科を取得
    const subject = SubjectCode.findByCode(this.querySubjectCode || '')

    this.studentPersonalStudyCategoryProgress.setCurrentSubjectCode(subject?.code || SubjectCode.LIST[0].code)
  }

  /**
   * 教材一覧取得
   *
   * @param gradeCode 学年コード
   * @param subjectCode 教科コード
   */
  private async getStudyCategories(gradeCode: string, subjectCode: string): Promise<void> {
    // 教材フィルターをアクティブにする
    this.isDisabledSelectStudyCategory = false

    // 教材一覧取得
    const studyCategories = await StudyCategoriesApi.getGenerals(gradeCode, subjectCode)

    // 指定の学年、教科に該当する教材がない場合、固定値を設定してDisabledにする
    if (studyCategories.length === 0) {
      this.filterStudyCategoryList = [this.OPTION_DISABLED]
      this.studentPersonalStudyCategoryProgress.setCurrentStudyCategoryCode(this.OPTION_DISABLED.value)
      this.isDisabledSelectStudyCategory = true
      return
    }

    // 教材フィルターの選択肢設定
    this.filterStudyCategoryList = studyCategories.map((studyCategory) => {
      return {
        // 教材IDをコードとして使用する
        value: String(studyCategory.id),
        label: studyCategory.name,
      }
    })

    // クエリ情報から教材を取得
    const studyCategory = this.filterStudyCategoryList.find(
      (studyCategory) => studyCategory.value === this.queryStudyCategoryId
    )

    // 教材フィルターの初期選択値を設定
    this.studentPersonalStudyCategoryProgress.setCurrentStudyCategoryCode(
      studyCategory?.value || this.filterStudyCategoryList[0].value
    )
  }

  /**
   * 単元一覧取得
   */
  private async getCurriculumUnits(): Promise<void> {
    // 既存値がある場合リセット
    if (this.progressCurriculumUnits) {
      this.progressCurriculumUnits = []
    }

    // 選択中の教材コード取得
    const studyCategoryCode = this.selectedStudyCategoryCode()

    // 教材コードが取得できない場合処理を終了
    if (!studyCategoryCode) {
      return
    }

    // 単元一覧取得
    const curriculumUnits = await CurriculumUnitsApi.getCurriculumUnits(this.studentId, Number(studyCategoryCode))

    // 単元一覧テーブルのデータを設定
    this.progressCurriculumUnits = curriculumUnits.map((curriculumUnit) => {
      const progress = curriculumUnit.progress || LearningProgress.NONE.code
      return {
        id: curriculumUnit.id,
        name: curriculumUnit.name,
        progressCode: progress,
      }
    })
  }

  /**
   * 選択中の学年コード
   *
   * @returns 学年コード
   */
  private selectedGradeCode(): string {
    return this.studentPersonalStudyCategoryProgress.selectedGradeCode()
  }

  /**
   * 選択中の教科コード
   *
   * @returns 教科コード
   */
  private selectedSubjectCode(): string {
    return this.studentPersonalStudyCategoryProgress.selectedSubjectCode()
  }

  /**
   * 選択中の教材コード
   *
   * @returns 教材コード
   */
  private selectedStudyCategoryCode(): string {
    return this.studentPersonalStudyCategoryProgress.selectedStudyCategoryCode()
  }

  /**
   * 戻るボタン押下時の処理
   */
  private eventClickBackButton(): void {
    // 保持されたクエリ情報を取得
    const prevPage = this.$prevPage.restorePage()

    if (!prevPage) {
      // クエリが存在しない場合、通常学習の進捗一覧へ遷移
      this.$router.push('/teacher/students/progress/general')
      return
    }

    // クエリを元に遷移
    this.$router.push({
      path: prevPage.path,
      query: prevPage.query,
    })
  }

  /**
   * 学年、教材指定変更時の処理
   */
  private async changeParamGradeSubject(): Promise<void> {
    this.$loading.start()

    // 現在の選択値を取得
    const gradeCode = this.selectedGradeCode()
    const subjectCode = this.selectedSubjectCode()

    // 学年コードと教科コードの選択状況でブロック
    if (gradeCode && subjectCode) {
      // 教材一覧取得(教材フィルター更新)
      await this.getStudyCategories(gradeCode, subjectCode)

      // 単元一覧取得
      await this.getCurriculumUnits()
    }

    this.$loading.complete()
  }

  /**
   * 学年、教材指定変更時の処理
   */
  private async changeParamStudyCategory(): Promise<void> {
    this.$loading.start()

    // 単元一覧取得
    await this.getCurriculumUnits()

    this.$loading.complete()
  }

  /**
   * ダウンロードボタン押下時の処理
   *
   * TODO: ダウンロード機能実装後に修正
   */
  private eventClickDownload(): void {
    alert('ダウンロード')
  }

  /**
   * 知識MAPタブクリック時の処理
   */
  private onClickKnowledgeMapTab(): void {
    this.$router.push({
      path: `/teacher/students/${this.studentId}/map`,
      query: this.$route.query,
    })
  }
}
